import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import './Login.css';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [otpRequired, setOtpRequired] = useState(false);
  const [otpSentTo, setOtpSentTo] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const payload = { email, password };
      if (otpRequired && otp) {
        payload.otp = otp;
      }

      const response = await authAPI.login(payload.email, payload.password, payload.otp);

      if (response.token) {
        navigate('/dashboard');
      } else if (response.operation === 'OTP') {
        setOtpRequired(true);
        setOtpSentTo(response.sent_to || '');
        // In dev mode, the backend returns the code — auto-fill it
        if (response.dev_code) {
          setOtp(String(response.dev_code));
        }
        setError('');
      } else {
        setError('Login failed. Please try again.');
      }
    } catch (err) {
      if (err.response?.status === 401) {
        if (otpRequired) {
          setError('Invalid OTP code. Please try again.');
        } else {
          setError('Invalid email or password');
        }
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setError('');
    setLoading(true);
    try {
      // Re-submit credentials without OTP to trigger a new code
      const response = await authAPI.login(email, password);
      if (response.operation === 'OTP') {
        setOtpSentTo(response.sent_to || '');
        setError('');
      }
    } catch (err) {
      setError('Failed to resend code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-wrapper">
      <div className="login-container-dark">
        <div className="login-logo">
          <img src="/logo.png" alt="Punt Club Logo" />
        </div>

        <h2 className="login-title">{otpRequired ? 'Verify Your Identity' : 'Sign In'}</h2>

        {!otpRequired && (
          <p className="login-subtitle">
            First time here? &nbsp;
            <a href="/register">Start a club</a>
          </p>
        )}

        {otpRequired && (
          <p className="login-description">
            We've sent a verification code to {otpSentTo || 'your phone'}. Enter it below to continue.
          </p>
        )}

        {!otpRequired && (
          <p className="login-description">
            The world's first &amp; best syndicate management platform!
          </p>
        )}

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          {!otpRequired ? (
            <>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  autoComplete="email"
                  placeholder="Enter your email"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  disabled={loading}
                />
              </div>
            </>
          ) : (
            <div className="form-group">
              <label htmlFor="otp">Verification Code</label>
              <input
                type="text"
                id="otp"
                name="otp"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                autoFocus
                autoComplete="one-time-code"
                placeholder="Enter 6-digit code"
                disabled={loading}
                maxLength={6}
                style={{ textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5em' }}
              />
            </div>
          )}

          <button type="submit" className="btn-warning" disabled={loading}>
            {loading && <span className="btn-spinner"></span>}
            {loading ? (otpRequired ? 'Verifying...' : 'Signing In...') : (otpRequired ? 'Verify' : 'Sign In')}
          </button>

          {otpRequired && (
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <button type="button" onClick={handleResendOtp} disabled={loading}
                style={{ background: 'none', border: 'none', color: '#fbbf24', cursor: 'pointer', fontSize: 14 }}>
                Resend Code
              </button>
              <span style={{ color: '#666', margin: '0 8px' }}>|</span>
              <button type="button" onClick={() => { setOtpRequired(false); setOtp(''); setError(''); }} disabled={loading}
                style={{ background: 'none', border: 'none', color: '#aaa', cursor: 'pointer', fontSize: 14 }}>
                Back to Login
              </button>
            </div>
          )}

          {!otpRequired && (
            <div className="forgot-password">
              <a href="/reset-password">Forgot your password?</a>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default Login;
